
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase, manageUserSession } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";

export const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Capture install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show install prompt
      deferredPrompt.prompt();
      
      // Wait for user choice
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        toast({
          title: "Instalação iniciada",
          description: "O aplicativo está sendo instalado no seu dispositivo",
        });
      }
      
      // Clear the deferredPrompt
      setDeferredPrompt(null);
    } else {
      // Mostrar instruções para instalação manual
      toast({
        title: "Instalação manual",
        description: "Para instalar, abra o menu do navegador e selecione 'Adicionar à tela inicial' ou 'Instalar aplicativo'",
      });
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log("Iniciando processo de login...");
      
      const { data: { session }, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      console.log("Resposta do login:", { session, error });

      if (error) {
        console.error("Login error:", error);
        toast({
          variant: "destructive",
          title: "Erro ao fazer login",
          description: error.message === "Invalid login credentials"
            ? "Email ou senha incorretos"
            : error.message,
        });
        return;
      }

      if (session) {
        console.log("Sessão criada, verificando limite...");
        
        // Verificar limite de sessões
        const canCreateSession = await manageUserSession(
          session.user.id,
          session.access_token
        );

        console.log("Resultado da verificação de sessão:", canCreateSession);

        if (!canCreateSession) {
          console.log("Limite de sessões atingido, fazendo logout...");
          await supabase.auth.signOut();
          toast({
            variant: "destructive",
            title: "Limite de sessões atingido",
            description: "Você já está logado em 3 dispositivos diferentes. Por favor, faça logout em um deles para continuar.",
          });
          return;
        }

        console.log("Login bem sucedido, redirecionando...");
        navigate("/adm/dashboard", { replace: true });
        
        // Adicionar toast de sucesso
        toast({
          title: "Login realizado com sucesso",
          description: "Bem-vindo ao sistema!",
        });
      } else {
        console.error("Sessão não criada após login bem-sucedido");
        toast({
          variant: "destructive",
          title: "Erro ao fazer login",
          description: "Não foi possível criar a sessão. Por favor, tente novamente.",
        });
      }
      
    } catch (error) {
      console.error("Erro inesperado durante login:", error);
      toast({
        variant: "destructive",
        title: "Erro ao fazer login",
        description: "Ocorreu um erro inesperado. Por favor, tente novamente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
        <div className="flex flex-col items-center justify-center w-full">
          <img 
            src="/lovable-uploads/74e142c8-a174-49c3-9a42-d96466fcb5c2.png" 
            alt="Solucionar Soldas Logo" 
            className="w-40 h-auto mb-4 mx-auto"
          />
          <h2 className="mb-6 text-center text-2xl font-bold text-[#004080]">
            Área Administrativa
          </h2>
        </div>
        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full"
                placeholder="seu@email.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Senha
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 w-full"
                placeholder="******"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#0073B7] hover:bg-[#004080]"
          >
            {isLoading ? "Entrando..." : "Entrar"}
          </Button>
        </form>
        
        {/* Sempre mostra o botão de instalar o aplicativo */}
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            className="flex items-center gap-2 border-[#0073B7] text-[#0073B7] hover:bg-[#0073B7]/10"
            onClick={handleInstallClick}
          >
            <Download className="h-4 w-4" />
            Baixar Aplicativo
          </Button>
        </div>
      </div>
    </div>
  );
};
